import { Stack } from '@mui/material';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';

import { useTranslation } from '@shared/i18n';
import AdvancedColor from '@shared/icons/AdvancedColor.svg?react';
import BackupColor from '@shared/icons/BackupColor.svg?react';
import ClipboardText from '@shared/icons/ClipboardText.svg?react';
import FileManagerColor from '@shared/icons/FileManagerColor.svg?react';
import FileSettings from '@shared/icons/FileSettings.svg?react';
import FolderSearch from '@shared/icons/FolderSearch.svg?react';
import Layers from '@shared/icons/Layers.svg?react';
import LockClose from '@shared/icons/LockClose.svg?react';
import LogsColor from '@shared/icons/LogsColor.svg?react';
import OverviewColor from '@shared/icons/OverviewColor.svg?react';
import PluginAddon from '@shared/icons/PluginAddon.svg?react';
import PluginColor from '@shared/icons/PluginColor.svg?react';
import Receipt from '@shared/icons/Receipt.svg?react';
import ReportingColor from '@shared/icons/ReportingColor.svg?react';
import RocketShip from '@shared/icons/RocketShipSolid.svg?react';
import SecurityColor from '@shared/icons/SecurityColor.svg?react';
import Server from '@shared/icons/Server.svg?react';
import ThemesColor from '@shared/icons/ThemesColor.svg?react';

import { siteDetailsQueryOptions, useSiteDetails } from 'api/site';

import { useOperationChecker } from 'component/hooks/useOperationAvailable';
import { IncomingAccountInvitesContainer } from 'component/partial/IncomingAccountInvitesContainer';
import { IncomingSiteTransfersContainer } from 'component/partial/IncomingSiteTransfersContainer';
import { Navigation, NavigationItem } from 'component/partial/Navigation';
import { OutgoingSiteTransferAlert } from 'component/partial/SiteDetails/OutgoingSiteTransferAlert';
import { SiteEnvironmentCard } from 'component/partial/SiteDetails/Overview/SiteEnvironmentCard';
import { SiteDetailsHeader } from 'component/partial/SiteDetailsHeader';
import { SiteStatus } from 'component/partial/SiteStatus';
import { UnpaidInvoicesAlert } from 'component/partial/UnpaidInvoicesAlert';

import { siteTabOperations } from './-site-tabs';

export const Route = createFileRoute('/n/_auth/sites/$siteId/_details')({
  beforeLoad: async ({ context, params }) => {
    // Populate the cache so link availability guards can read site links, but let
    // failures fall through to the component query, which routes them to ErrorHandler.
    await context.queryClient.ensureQueryData(siteDetailsQueryOptions(params.siteId)).catch(() => {
      // Intentionally ignored - handled at render time
    });
  },
  component: SiteDetailsLayout,
});

function SiteDetailsLayout() {
  const { siteId } = Route.useParams();
  const { data } = useSiteDetails(siteId);
  const siteDetails = data?.result;
  const { t } = useTranslation();
  const isAvailable = useOperationChecker({ siteId });
  const showTab = (tab: keyof typeof siteTabOperations) =>
    isAvailable(...siteTabOperations[tab](siteId));

  const navigationMenuItems = [
    {
      solidIcon: <FolderSearch />,
      colorIcon: <OverviewColor />,
      title: t('common.overview'),
      to: `/n/sites/${siteId}/overview`,
    },
    showTab('plugins')
      ? {
          solidIcon: <PluginAddon />,
          colorIcon: <PluginColor />,
          title: t('common.plugin_plural'),
          to: `/n/sites/${siteId}/plugins`,
        }
      : null,
    showTab('themes')
      ? {
          solidIcon: <Layers />,
          colorIcon: <ThemesColor />,
          title: t('common.theme_plural'),
          to: `/n/sites/${siteId}/themes`,
        }
      : null,
    showTab('fileManager')
      ? {
          solidIcon: <FileSettings />,
          colorIcon: <FileManagerColor />,
          title: t('site_details.file_manager.title'),
          to: `/n/sites/${siteId}/file-manager`,
        }
      : null,
    showTab('backups')
      ? {
          solidIcon: <Server />,
          colorIcon: <BackupColor />,
          title: t('common.backup_plural'),
          to: `/n/sites/${siteId}/backups`,
        }
      : null,
    showTab('logs')
      ? {
          solidIcon: <ClipboardText />,
          colorIcon: <LogsColor />,
          title: t('common.log_plural'),
          to: `/n/sites/${siteId}/logs`,
        }
      : null,
    showTab('reporting')
      ? {
          solidIcon: <Receipt />,
          colorIcon: <ReportingColor />,
          title: t('common.reporting'),
          to: `/n/sites/${siteId}/reporting`,
        }
      : null,
    showTab('security')
      ? {
          solidIcon: <LockClose />,
          colorIcon: <SecurityColor />,
          title: t('common.security'),
          to: `/n/sites/${siteId}/security`,
        }
      : null,
    showTab('advanced')
      ? {
          solidIcon: <RocketShip />,
          colorIcon: <AdvancedColor />,
          title: t('common.advanced'),
          to: `/n/sites/${siteId}/advanced`,
        }
      : null,
  ].filter(Boolean) satisfies (NavigationItem | null)[] as NavigationItem[];

  return (
    <Stack sx={{ gap: 6 }}>
      <SiteDetailsHeader />
      <UnpaidInvoicesAlert />
      <IncomingSiteTransfersContainer />
      <IncomingAccountInvitesContainer />
      <OutgoingSiteTransferAlert siteId={siteId} />
      <SiteStatus siteDetails={siteDetails} />
      <SiteEnvironmentCard siteId={siteId} />
      <Navigation items={navigationMenuItems} />
      <Suspense>
        <Outlet />
      </Suspense>
    </Stack>
  );
}
